import { useState, useEffect, useCallback, type ReactNode } from 'react'
import type { ThemeConfig } from './types'
import { defaultThemeConfig } from './defaults'
import { themeApi } from './themeApi'
import { applyThemeVars } from './applyTheme'
import { ThemeConfigProvider } from './ThemeConfigContext'

interface ThemeProviderProps {
  children: ReactNode
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [config, setConfig] = useState<ThemeConfig>(defaultThemeConfig)
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    async function loadTheme() {
      try {
        const loadedConfig = await themeApi.getTheme()
        setConfig(loadedConfig)
      } catch {
        // Use default on error, already set
      } finally {
        setIsReady(true)
      }
    }
    loadTheme()
  }, [])

  // Apply CSS custom properties directly to :root on every config change
  useEffect(() => {
    applyThemeVars(config)
  }, [config])

  const handleConfigChange = useCallback((newConfig: ThemeConfig) => {
    setConfig(newConfig)
  }, [])

  // Show nothing briefly while loading to avoid flash of wrong theme
  if (!isReady) {
    return null
  }

  return (
    <ThemeConfigProvider config={config} onConfigChange={handleConfigChange}>
      {children}
    </ThemeConfigProvider>
  )
}
